import { useEffect, useState } from 'react';

type Sync = { last_call: string | null; last_sync: string | null };
type Health = {
  syncs?: { wxc: Sync; wxcc: Sync };
  last_cdr: string | null;
  minutes_since_last_call: number | null;
  ingest_checkpoint_age_min: number | null;
  months: { ym: string; status: string; rows: number }[];
  missing_days: string[];
  hours_covered_3d: number;
  hours_expected_3d: number;
};

const fmt = (iso: string | null) => { if (!iso) return '—'; const d = new Date(iso); return isNaN(+d) ? '—' : d.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); };
function Tile({ v, l, tone }: { v: any; l: string; tone?: 'ok' | 'warn' | 'err' }) {
  const c = tone === 'err' ? 'text-red-600 dark:text-red-400' : tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : tone === 'ok' ? 'text-green-600 dark:text-green-400' : '';
  return <div className="card p-3"><div className={`text-xl font-semibold ${c}`}>{v}</div><div className="text-[11px] text-gray-500">{l}</div></div>;
}

export default function CdrStatus() {
  const [h, setH] = useState<Health | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [at, setAt] = useState<string>('');

  async function load() {
    setBusy(true); setErr(null);
    try {
      const r = await fetch('/cdr/api/ingesthealth', { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) { setErr(j.error || 'error'); setH(null); } else { setH(j); setAt(new Date().toLocaleString()); }
    } catch (e: any) { setErr(String(e)); } finally { setBusy(false); }
  }
  useEffect(() => { load(); }, []);

  const fresh = (h?.minutes_since_last_call ?? 99999) < 180;
  const cpOk = (h?.ingest_checkpoint_age_min ?? 99999) < 180;
  const gaps = h?.missing_days?.length || 0;
  const ago = (m: number | null) => m == null ? '—' : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="text-base font-semibold">System status</div>
          <div className="text-[11px] text-gray-500">Live health of the {(typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('clbcdr')) ? 'Long Beach' : 'Santa Monica'} CDR pipeline</div>
        </div>
        <button onClick={load} className="ml-auto px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs">{busy ? 'Refreshing…' : 'Refresh'}</button>
        {at && <span className="text-[11px] text-gray-400 w-full md:w-auto">Checked {at}</span>}
      </div>

      {err && <div className="text-xs text-red-500">Sign in to the CDR portal to view ({err}).</div>}

      {h && (<>
        {h.syncs && (
          <div className="grid md:grid-cols-2 gap-2">
            {([['Webex Calling (WxC)', h.syncs.wxc, '#2f6fed'], ['Webex Contact Center (WxCC)', h.syncs.wxcc, '#00CF64']] as [string, Sync, string][]).map(([name, s, col]) => (
              <div key={name} className="card p-3">
                <div className="flex items-center gap-2 mb-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: col }} /><div className="text-sm font-semibold">{name}</div></div>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <div><div className="text-[11px] text-gray-500">Last data sync</div><div className="text-sm font-medium">{fmt(s.last_sync)}</div></div>
                  <div><div className="text-[11px] text-gray-500">Last call recorded</div><div className="text-sm font-medium">{fmt(s.last_call)}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Tile v={fresh ? 'Online' : 'Stale'} l="Ingest pipeline" tone={fresh ? 'ok' : 'warn'} />
          <Tile v={ago(h.minutes_since_last_call)} l="Last call recorded" tone={fresh ? 'ok' : 'warn'} />
          <Tile v={ago(h.ingest_checkpoint_age_min)} l="Ingest checkpoint" tone={cpOk ? 'ok' : 'warn'} />
          <Tile v={`${h.hours_covered_3d}/${h.hours_expected_3d}`} l="Hours covered (3d)" tone={h.hours_covered_3d >= h.hours_expected_3d - 2 ? 'ok' : 'warn'} />
        </div>

        <div className="card p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Data gaps</div>
            <span className={`text-xs ${gaps ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>{gaps ? `${gaps} day(s) missing` : 'No gaps'}</span>
          </div>
          {gaps > 0
            ? <div className="text-[11px] text-gray-500 font-mono break-words">{h.missing_days.join(' · ')}</div>
            : <div className="text-[11px] text-gray-400">Every day in the covered range has records.</div>}
        </div>

        {h.months?.length > 0 && (
          <div className="card p-3 overflow-auto">
            <div className="text-sm font-semibold mb-2">Historical import (by month)</div>
            <table className="w-full text-xs">
              <thead className="text-gray-500"><tr><th className="text-left px-2 py-1">Month</th><th className="text-left px-2 py-1">Status</th><th className="text-right px-2 py-1">Rows</th></tr></thead>
              <tbody>
                {h.months.map((m) => (
                  <tr key={m.ym} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="px-2 py-1 font-mono">{m.ym}</td>
                    <td className="px-2 py-1"><span className={m.status === 'done' ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}>{m.status}</span></td>
                    <td className="px-2 py-1 text-right">{(m.rows || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-[11px] text-gray-400">Ingest runs automatically (hourly + catch-up). "Stale" simply means no new call in the last 3 hours — normal outside business hours.</div>
      </>)}
    </div>
  );
}
